import { format } from 'date-fns';

/**
 * Formats a date string in the user's local timezone
 * Handles both date-only strings (YYYY-MM-DD) and full datetime strings
 * 
 * @param dateString - Date string from backend (may be UTC)
 * @param formatString - Format string for date-fns format function
 * @returns Formatted date string in local timezone
 */
export const formatLocalDate = (dateString: string, formatString: string = 'MMM dd, yyyy'): string => {
  if (!dateString) return '';
  
  try {
    // If the date string is just a date (YYYY-MM-DD), parse it as local time
    // Otherwise, parse it normally (which will handle UTC strings correctly)
    let date: Date;
    
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // Date-only string: parse as local time to avoid UTC conversion
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      // ISO string without timezone: treat as local time
      date = new Date(dateString);
    } else {
      // Full datetime string with timezone (or UTC): parse normally
      // JavaScript will handle UTC conversion, then format will display in local timezone
      date = new Date(dateString);
    }
    
    // Validate the date
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateString}`);
      return dateString; // Return original string if invalid
    }
    
    // Format in local timezone (date-fns format uses local timezone by default)
    return format(date, formatString);
  } catch (error) {
    console.error(`Error formatting date: ${dateString}`, error);
    return dateString; // Return original string on error
  }
};

/**
 * Formats a datetime string with time in the user's local timezone
 */
export const formatLocalDateTime = (dateString: string, formatString: string = 'MMM dd, yyyy HH:mm'): string => {
  return formatLocalDate(dateString, formatString);
};

/**
 * Formats a datetime string in a specific IANA timezone (e.g. 'UTC', 'Asia/Kolkata')
 * Uses Intl.DateTimeFormat for accurate timezone conversion.
 *
 * @param dateString - ISO date string from backend
 * @param timezone - IANA timezone string (e.g. 'UTC', 'Asia/Kolkata')
 * @param formatString - Simple format tokens: 'HH:mm:ss', 'MMM dd, yyyy', 'MMM dd, yyyy HH:mm'
 * @returns Formatted date string in the specified timezone
 */
export const formatInTimezone = (dateString: string, timezone: string, formatString: string = 'MMM dd, yyyy HH:mm'): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    // Get parts in the target timezone
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const get = (type: string) => parts.find(p => p.type === type)?.value || '';
    const year = get('year');
    const month = get('month');
    const day = get('day');
    const hour = get('hour') === '24' ? '00' : get('hour');
    const minute = get('minute');
    const second = get('second');

    return formatString
      .replace('yyyy', year)
      .replace('MMM', month)
      .replace('dd', day)
      .replace('HH', hour)
      .replace('mm', minute)
      .replace('ss', second);
  } catch (error) {
    console.error(`Error formatting date in timezone ${timezone}: ${dateString}`, error);
    return dateString;
  }
};

/**
 * Converts a UTC date and hour to local timezone and returns the local date and hour
 * @param utcDate - Date string in YYYY-MM-DD format (UTC)
 * @param utcHour - Hour in UTC (0-23)
 * @returns Object with localDate (YYYY-MM-DD) and localHour (0-23)
 */
export const convertUtcToLocalHour = (utcDate: string, utcHour: number): { localDate: string; localHour: number } => {
  try {
    // Create a UTC date from the date string and hour
    const [year, month, day] = utcDate.split('-').map(Number);
    const utcDateTime = new Date(Date.UTC(year, month - 1, day, utcHour, 0, 0));
    
    // Get local time components
    const localYear = utcDateTime.getFullYear();
    const localMonth = String(utcDateTime.getMonth() + 1).padStart(2, '0');
    const localDay = String(utcDateTime.getDate()).padStart(2, '0');
    const localHour = utcDateTime.getHours();
    
    return {
      localDate: `${localYear}-${localMonth}-${localDay}`,
      localHour: localHour
    };
  } catch (error) {
    console.error(`Error converting UTC to local hour: ${utcDate} ${utcHour}`, error);
    return { localDate: utcDate, localHour: utcHour };
  }
};

